import clsx from 'clsx';
import React from 'react';
import PropTypes from 'prop-types';
import elementType from 'prop-types-extra/lib/elementType';
import warning from 'warning';
import { getClassSet, prefix, splitBsPropsAndOmit } from '../bootstrap/utils/bootstrapUtils';
import createChainedFunction from '../bootstrap/utils/createChainedFunction';
import { useTabContainer, useTabContent, TabContainerProvider } from '../bootstrap/context/TabContext';
import Fade from '../Fade/Fade.js';
import { jsx as _jsx } from "react/jsx-runtime";
class TabPane extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.handleEnter = this.handleEnter.bind(this);
    this.handleExited = this.handleExited.bind(this);
    this.in = false;
  }
  componentDidMount() {
    if (this.shouldBeIn()) {
      // In lieu of the action event firing.
      this.handleEnter();
    }
  }
  componentDidUpdate() {
    if (this.in) {
      if (!this.shouldBeIn()) {
        // We shouldn't be active any more. Notify the parent.
        this.handleExited();
      }
    } else if (this.shouldBeIn()) {
      // We are the active child. Notify the parent.
      this.handleEnter();
    }
  }
  componentWillUnmount() {
    if (this.in) {
      // In lieu of the action event firing.
      this.handleExited();
    }
  }
  handleEnter() {
    const {
      tabContent
    } = this.props;
    if (!tabContent) {
      return;
    }
    this.in = tabContent.onPaneEnter(this, this.props.eventKey);
  }
  handleExited() {
    const {
      tabContent
    } = this.props;
    if (!tabContent) {
      return;
    }
    tabContent.onPaneExited(this);
    this.in = false;
  }
  getAnimation() {
    if (this.props.animation != null) {
      return this.props.animation;
    }
    const {
      tabContent
    } = this.props;
    return tabContent && tabContent.animation;
  }
  isActive() {
    const {
      tabContent
    } = this.props;
    const activeKey = tabContent && tabContent.activeKey;
    return this.props.eventKey === activeKey;
  }
  shouldBeIn() {
    return this.getAnimation() && this.isActive();
  }
  render() {
    const {
      eventKey,
      className,
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      onExited,
      mountOnEnter: propsMountOnEnter,
      unmountOnExit: propsUnmountOnExit,
      tabContainer,
      tabContent,
      ...props
    } = this.props;
    const [bsProps, elementProps] = splitBsPropsAndOmit(props, ['animation']);
    const active = this.isActive();
    const animation = this.getAnimation();
    const mountOnEnter = propsMountOnEnter != null ? propsMountOnEnter : tabContent && tabContent.mountOnEnter;
    const unmountOnExit = propsUnmountOnExit != null ? propsUnmountOnExit : tabContent && tabContent.unmountOnExit;
    if (!active && !animation && unmountOnExit) {
      return null;
    }
    const Transition = animation === true ? Fade : animation || null;
    if (tabContent) {
      bsProps.bsClass = prefix(tabContent, 'pane');
    }
    const classes = {
      ...getClassSet(bsProps),
      active
    };
    if (tabContainer) {
      process.env.NODE_ENV !== "production" ? warning(!elementProps.id && !elementProps['aria-labelledby'], 'In the context of a `<TabContainer>`, `<TabPanes>` are given ' + 'generated `id` and `aria-labelledby` attributes for the sake of ' + 'proper component accessibility. Any provided ones will be ignored. ' + 'To control these attributes directly provide a `generateChildId` ' + 'prop to the parent `<TabContainer>`.') : void 0;
      elementProps.id = tabContainer.getPaneId(eventKey);
      elementProps['aria-labelledby'] = tabContainer.getTabId(eventKey);
    }
    const pane = /*#__PURE__*/_jsx("div", {
      ...elementProps,
      role: "tabpanel",
      "aria-hidden": !active,
      className: clsx(className, classes)
    });
    if (Transition) {
      const exiting = tabContent && tabContent.exiting;
      return /*#__PURE__*/_jsx(TabContainerProvider, {
        value: null,
        children: /*#__PURE__*/_jsx(Transition, {
          in: active && !exiting,
          onEnter: createChainedFunction(this.handleEnter, onEnter),
          onEntering: onEntering,
          onEntered: onEntered,
          onExit: onExit,
          onExiting: onExiting,
          onExited: createChainedFunction(this.handleExited, onExited),
          mountOnEnter: mountOnEnter,
          unmountOnExit: unmountOnExit,
          children: pane
        })
      });
    }
    return /*#__PURE__*/_jsx(TabContainerProvider, {
      value: null,
      children: pane
    });
  }
}
TabPane.defaultProps = {
  bsClass: 'tab-pane'
};
const TabPaneWithContext = /*#__PURE__*/React.forwardRef((props, ref) => {
  const tabContainer = useTabContainer();
  const tabContent = useTabContent();
  return /*#__PURE__*/_jsx(TabPane, {
    ...props,
    tabContainer: tabContainer,
    tabContent: tabContent,
    ref: ref
  });
});
TabPaneWithContext.displayName = 'TabPane';
export default TabPaneWithContext;